<template>
  <div v-loading="isLoading"
       class="align-items-stretch d-flex flex-column flex-grow-1 flex-nowrap flex-basis-0">
    <TabSelection :date-range-end="dateRangeEnd"
                  :date-range-start="dateRangeStart"
                  :should-show-archive="false"
                  :should-show-last-year="false"
                  :should-show-month="true"
                  :should-show-year="true"
                  :should-show-custom="true"
                  :should-show-csv-export="true"
                  @on-csv-click="exportCsv"
                  @change="onTabChange">
    </TabSelection>
    <AgGridVue class="flex-grow-1 flex-basis-0"
               :class="getThemeClass()"
               :grid-options="gridOptions"
               :modules="modules"
               :get-row-id="getRowId"
               @drag-stopped="onDragStopped"
               @grid-ready="onGridReady"
               @row-selected="onRowSelected">
    </AgGridVue>
    <Pagination v-if="gridApi"
                :initial-per-page="gridOptions.paginationPageSize"
                :portlet-id="portletId"
                :grid-api="gridApi"
                @per-page-changed="updatePerPage">
    </Pagination>
  </div>
</template>

<script>
  import { CsvExportModule } from '@ag-grid-community/csv-export';
  import { AgGridVue } from '@ag-grid-community/vue3/compat';
  import { ServerSideRowModelModule } from '@ag-grid-enterprise/server-side-row-model';
  import { getAgGridThemeClass, translator } from '~/zf/frontend/common/mixins';
  import '../../../vendor/ag-grid/bootstrap';
  import SystemListEvent from '~/zf/frontend/vcom/events/SystemListEvent';
  import defaultGridOptionsTable from '~/zf/frontend/vendor/ag-grid/defaultGridOptionsTable';
  import WindowProvider from '~/zf/frontend/common/window/WindowProvider';
  import PerformanceOverviewService from '../PerformanceOverviewService';
  import Pagination from '../../../common/ag-grid/pagination/Pagination.vue';
  import TimeRange from '../../../common/portlet/TimeRange';
  import TabSelection from '../../../common/portlet/TabSelection.vue';
  import ColumnId from '../const/ColumnId';
  import EventService from '~/zf/frontend/common/event/EventService';
  import UpdateServiceEvent from '~/zf/frontend/vcom/events/UpdateServiceEvent';
  import PortalEvent from '~/zf/frontend/vcom/events/PortalEvent';
  import PortletEvent from '~/zf/frontend/vcom/events/PortletEvent';
  import { canView } from '~/zf/frontend/common/acl/AclService';
  import AclModule from '~/zf/frontend/common/acl/const/AclModule';
  import PerPageOption from '~/zf/frontend/common/ag-grid/pagination/PerPageOption';
  import VcomProvider from '../../../VcomProvider';
  import SettingsService from './SettingsService';
  import RowModelType from '~/zf/frontend/common/ag-grid/const/RowModelType';

  import AlarmCounter from '../cellRenderer/AlarmCounter.vue';
  import DeviationRatedActual from '../cellRenderer/DeviationRatedActual.vue';
  import InvertersBar from '../cellRenderer/InvertersBar.vue';
  import LastDataInput from '../cellRenderer/LastDataInput.vue';
  import PercentageBar from '../cellRenderer/PercentageBar.vue';
  import SparklineChartIrradiation from '../cellRenderer/SparklineChartIrradiation.vue';
  import SparklineChartIrradiationSatellite from '../cellRenderer/SparklineChartIrradiationSatellite.vue';
  import SparklineChartYield from '../cellRenderer/SparklineChartYield.vue';
  import StatusIcons from '../cellRenderer/StatusIcons.vue';
  import System from '../cellRenderer/System.vue';

  export default {
    name: 'PerformanceOverviewTable',
    compatConfig: {
      MODE: 3,
    },
    components: {
      Pagination,
      AgGridVue,
      TabSelection,
      AlarmCounter, // eslint-disable-line vue/no-unused-components
      DeviationRatedActual, // eslint-disable-line vue/no-unused-components
      InvertersBar, // eslint-disable-line vue/no-unused-components
      LastDataInput, // eslint-disable-line vue/no-unused-components
      PercentageBar, // eslint-disable-line vue/no-unused-components
      StatusIcons, // eslint-disable-line vue/no-unused-components
      System, // eslint-disable-line vue/no-unused-components
      SparklineChartIrradiation, // eslint-disable-line vue/no-unused-components
      SparklineChartIrradiationSatellite, // eslint-disable-line vue/no-unused-components
      SparklineChartYield, // eslint-disable-line vue/no-unused-components
    },
    mixins: [getAgGridThemeClass, translator],
    props: {
      portletUId: {
        required: true,
        type: String,
      },
      selectedColumns: {
        type: Array,
        required: true,
      },
      initialPageSize: {
        type: Number,
        required: false,
      },
    },
    expose: ['updateColumns'],
    data() {
      return {
        isLoading: false,
        gridOptions: {},
        gridApi: null,
        timeRange: TimeRange.TODAY,
        endDate: undefined,
        startDate: undefined,
        dateRangeStart: undefined,
        dateRangeEnd: undefined,
        modules: [
          CsvExportModule,
          ServerSideRowModelModule,
        ],
        monitoringAclColumns: [
          ColumnId.STATUS,
          ColumnId.alarmsToday,
          ColumnId.alarmsYesterday,
          ColumnId.alarmsLastWeek,
        ],
        hiddenColumnsForInvestors: [
          ColumnId.alarmsToday,
          ColumnId.alarmsYesterday,
          ColumnId.alarmsLastWeek,
          ColumnId.DATAINPUT,
          ColumnId.E_SIM_DIFF_PERC,
          ColumnId.LAST_DATAINPUT_LOCALTIME,
        ],
        noSystemSelectable: true,
        wasRowSelectedProgrammatically: false,
      };
    },
    computed: {
      portletId() {
        return `portlet_${this.portletUId}`;
      },
      csvFileName() {
        if (this.dateRangeStart === this.dateRangeEnd) {
          return `performanceOverview_${this.dateRangeStart}`;
        }

        return `performanceOverview_${this.dateRangeStart}_${this.dateRangeEnd}`;
      },
    },
    beforeMount() {
      const gridOptions = {
        columnDefs: PerformanceOverviewService.getColumns(this.selectedColumns),
        defaultColDef: {
          lockPinned: true,
          resizable: false,
          unSortIcon: true,
          filter: false,
          sortable: true,
        },
        suppressDragLeaveHidesColumns: true,
        suppressMovableColumns: false,
        rowModelType: RowModelType.SERVER_SIDE,
        cacheBlockSize: this.initialPageSize ?? PerPageOption.TWENTY_FIVE,
        paginationPageSize: this.initialPageSize ?? PerPageOption.TWENTY_FIVE,
        rowSelection: 'single',
      };
      this.gridOptions = defaultGridOptionsTable(gridOptions);
    },
    unmounted() {
      EventService.removeListener(SystemListEvent.FILTER_CHANGED, { callback: this.reloadTable });
      EventService.removeListener(UpdateServiceEvent.RECEIVED, { callback: this.reloadTable });
      EventService.removeListener(PortalEvent.CURRENT_DAY_PASSED, { callback: this.reloadTable });
      EventService.removeListener(SystemListEvent.SYSTEM_SELECTED, { callback: this.selectRowBySystemId });
      EventService.removeListener(SystemListEvent.SYSTEM_DESELECTED, { callback: this.deselectRows });
      EventService.removeListener(PortletEvent.RESIZE_STOPPED, {
        callback: this.resize,
        target: WindowProvider.document.getElementById(this.portletId),
      });
    },
    methods: {
      onGridReady(params) {
        this.gridApi = params.api;
        this.gridApi.setGridOption('serverSideDatasource', this.getDataSource());
        this.gridApi.hidePopupMenu();
        EventService.addListener(PortletEvent.RESIZE_STOPPED, {
          callback: this.resize,
          target: WindowProvider.document.getElementById(this.portletId),
        });
        EventService.addListener(SystemListEvent.FILTER_CHANGED, { callback: this.reloadTable });
        EventService.addListener(UpdateServiceEvent.RECEIVED, { callback: this.reloadTable });
        EventService.addListener(PortalEvent.CURRENT_DAY_PASSED, { callback: this.reloadTable });
        EventService.addListener(SystemListEvent.SYSTEM_SELECTED, { callback: this.selectRowBySystemId });
        EventService.addListener(SystemListEvent.SYSTEM_DESELECTED, { callback: this.deselectRows });
        this.hideColumnsOnTodayTab();
        this.hideColumnsForInvestors();
        this.hideMonitoringAclColumns();
        this.resetColumnState();
      },
      getRowId(params) {
        return params.data.DT_RowId;
      },
      resetColumnState() {
        const columnState = this.gridApi.getColumnState();
        this.gridApi.setGridOption('columnDefs', PerformanceOverviewService.getColumns(this.selectedColumns));
        if (columnState !== null) {
          this.gridApi.applyColumnState({ state: columnState, applyOrder: true });
        }
      },
      onRowSelected(event) {
        if (event.node.selected === false) {
          return;
        }
        if (this.wasRowSelectedProgrammatically === true) {
          this.wasRowSelectedProgrammatically = false;
          return;
        }
        EventService.dispatch(SystemListEvent.SYSTEM_SELECTED, {
          data: {
            id: event.data.DT_RowId,
            systemName: event.data.bezeichnung,
          },
        });
      },
      selectRowBySystemId(event) {
        this.noSystemSelectable = true;
        this.gridApi.forEachNode((node) => {
          if (node.data !== undefined && event.detail.id === node.data.DT_RowId) {
            this.noSystemSelectable = false;
            if (node.isSelected() === true) {
              return;
            }
            node.setSelected(true, true);
            this.wasRowSelectedProgrammatically = true;
          }
        });

        if (this.noSystemSelectable === true) {
          this.deselectRows();
        }
      },
      deselectRows() {
        this.gridApi.deselectAll();
      },
      resize() {
        this.gridApi.sizeColumnsToFit();
      },
      getDataSource() {
        return {
          getRows: async (params) => {
            this.isLoading = true;
            try {
              const {
                data, dateRangeEnd, dateRangeStart, totalRecords,
              } = await PerformanceOverviewService.load(
                this.timeRange,
                this.gridApi.paginationGetPageSize(),
                params.request.startRow,
                params.request.sortModel,
                this.endDate,
                this.startDate,
                this.selectedColumns,
              );
              this.dateRangeEnd = dateRangeEnd;
              this.dateRangeStart = dateRangeStart;
              this.gridApi.setGridOption('context', { timerange: this.timeRange });
              if (totalRecords > 0) {
                this.gridApi.setGridOption('suppressNoRowsOverlay', true);
                this.gridApi.hideOverlay();
              } else {
                this.gridApi.setGridOption('suppressNoRowsOverlay', false);
                this.gridApi.showNoRowsOverlay();
              }
              params.success({ rowData: data, rowCount: totalRecords });
            } catch {
              params.fail();
            } finally {
              this.isLoading = false;
            }
          },
        };
      },
      reloadTable() {
        this.gridApi.refreshServerSide({ purge: true });
        this.hideColumnsOnTodayTab();
      },
      updatePerPage(value) {
        PerformanceOverviewService.savePaginationSize(value, this.portletUId);
        this.gridApi.setGridOption('cacheBlockSize', value);
        this.gridApi.setGridOption('paginationPageSize', value);
        this.resetColumnState();
      },
      onTabChange(dateTime, endDate, startDate) {
        this.timeRange = dateTime;
        this.endDate = endDate;
        this.startDate = startDate;
        this.reloadTable();
      },
      exportCsv() {
        const columns = this.gridApi.getAllDisplayedColumns();
        const filteredColumns = columns.filter((column) => column.getColId() !== ColumnId.STATUS);

        const utcColumn = this.gridApi.getColumn(ColumnId.LOCAL_TIMEZONE_OFFSET);

        const params = {
          columnKeys: [...filteredColumns, utcColumn],
          fileName: this.csvFileName,
          columnSeparator: '\t',
        };

        this.gridApi.exportDataAsCsv(params);
      },
      updateColumns(selectedColumns) {
        this.gridApi.setGridOption('columnDefs', PerformanceOverviewService.getColumns(selectedColumns));
        this.reloadTable();
        this.resize();
      },
      onDragStopped(dragStoppedEvent) {
        const columnState = dragStoppedEvent.api.getColumnState();
        const columnIds = columnState.map((column) => column.colId);

        SettingsService.saveColumnSettings(this.portletUId, columnIds);
      },
      hideColumnsOnTodayTab() {
        if (this.timeRange === TimeRange.TODAY) {
          this.todayColumnsVisible(false);
          this.onlyTodayColumnsVisible(true);
        } else {
          this.todayColumnsVisible(true);
          this.onlyTodayColumnsVisible(false);
        }
      },
      todayColumnsVisible(state) {
        const todayColumnsToHide = PerformanceOverviewService.getHiddenTodayColumns();
        this.gridApi.setColumnsVisible(todayColumnsToHide, state);
      },
      onlyTodayColumnsVisible(state) {
        const onlyTodayColumns = PerformanceOverviewService.getOnlyTodayColumns();
        this.gridApi.setColumnsVisible(onlyTodayColumns, state);
      },
      hideColumnsForInvestors() {
        if (VcomProvider.isInvestor) {
          this.gridApi.setColumnsVisible(this.hiddenColumnsForInvestors, false);
        }
      },
      async hideMonitoringAclColumns() {
        const canViewMonitoring = await canView(AclModule.MONITORING);
        if (canViewMonitoring === false) {
          this.gridApi.setColumnsVisible(this.monitoringAclColumns, false);
        }
      },
    },
  };
</script>
